import React, { PropsWithChildren, useState, useEffect } from "react";

import useGrpcQuery from "../../../lib/hooks/useGrpcQuery";
import { useDebounce } from "../../../lib/hooks/useDebounce";

import { Button } from "../../shared/Button/Button";
import { HandleLoadingState } from "../../shared/HandleLoadingState/HandleLoadingState";
import { LoadingBox } from "../../shared/LoadingBox/LoadingBox";
import ModalSidebar from "../../Modals/ModalSidebar/ModalSidebar";
import { SearchBox } from "../../shared/SearchBox/SearchBox";
import { CheckboxButton } from "../../shared/CheckboxButton/CheckboxButton";
import { MembersList } from "./MembersList";

import {
  ListGroupCandidatesRequest,
  useGroupsRequests,
} from "../../../api/grpc/permissions/useGroupsRequests";
import {
  ListPermissionGroupCandidatesResponse,
  PermissionGroupMember,
} from "../../../api/grpc/permissions/permissions";
import { GroupFormik } from "./lib/useGroupFormik";

import "./MembersModal.scss";

const PAGE_SIZE = 20;

interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
}

interface Props {
  formik: GroupFormik;
  onClose: () => void;
  mode: "add" | "edit";
}

export const AddMembersModal = ({
  formik,
  onClose,
  mode,
}: PropsWithChildren<Props>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    mode === "edit"
      ? formik.values.addMembers || []
      : formik.values.members || []
  );

  const { listPermissionGroupCandidates } = useGroupsRequests();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, error, loading } = useGrpcQuery<
    ListGroupCandidatesRequest,
    ListPermissionGroupCandidatesResponse
  >({
    method: listPermissionGroupCandidates,
    variables: {
      id: formik.values.id || "",
      page: page,
      pageSize: PAGE_SIZE,
      search: debouncedSearchTerm,
    },
  });

  const selectedMembers = new Set(selectedUserIds);

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (data?.candidates) {
      const newUsers: User[] = data.candidates.map(
        (member: PermissionGroupMember) => ({
          id: member.id,
          name: `${member.firstName} ${member.lastName}`.trim() || member.email,
          email: member.email,
          role: "Member",
        })
      );

      if (page === 1) {
        setUsers(newUsers);
      } else {
        setUsers((prevUsers) => {
          const existingIds = new Set(prevUsers.map((u) => u.id));
          const uniqueNewUsers = newUsers.filter((u) => !existingIds.has(u.id));
          return [...prevUsers, ...uniqueNewUsers];
        });
      }

      const totalCount = data.count ?? 0;
      setHasMore(page * PAGE_SIZE < totalCount);
    }
  }, [data, page]);

  const filteredUsers = users;

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (filteredUsers.length === 0 || loading || error) {
      return;
    }

    const allUserIds = filteredUsers.map((user) => user.id);
    const allSelected = allUserIds.every((id) => selectedUserIds.includes(id));

    if (allSelected) {
      setSelectedUserIds((prev) =>
        prev.filter((id) => !allUserIds.includes(id))
      );
    } else {
      setSelectedUserIds((prev) => {
        const newIds = [...prev];
        allUserIds.forEach((id) => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
    }
  };

  const loadMoreUsers = () => {
    if (!loading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleAddMembers = async () => {
    if (mode === "edit") {
      formik.setFieldValue("addMembers", selectedUserIds);
    } else {
      formik.setFieldValue("members", selectedUserIds);
    }
    onClose();
  };

  const SelectAllMembers = () => {
    const allUserIds = filteredUsers.map((user) => user.id);
    const allSelected = allUserIds.every((id) => selectedUserIds.includes(id));

    return (
      <div className="SelectAllMembers d-flex align-items-center">
        <CheckboxButton
          checked={allSelected}
          onChange={handleSelectAll}
          hideLabel
          id="select-all-members"
          name="select-all-members"
        />
      </div>
    );
  };

  return (
    <ModalSidebar
      isOpen={true}
      onToggle={() => onClose()}
      title="Add members"
      onSubmit={handleAddMembers}
      submitButtonLabel="Add members"
      isDisabled={selectedMembers.size === 0}
      headerDescription="Select and add group members"
      footerDescription={`${selectedMembers.size} members selected`}
      footerDescriptionClassName="MembersModal__footerDescription"
      className="MembersModal"
    >
      <div className="d-flex flex-col gap-1">
        <div className="flex-a-center justify-content-between">
          <SelectAllMembers />

          <SearchBox
            searchValue={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            clearSearchValue={() => setSearchTerm("")}
          />
        </div>

        <HandleLoadingState
          loading={loading && page === 1}
          loadingPlaceholder={<LoadingBox count={3} minHeight={60} />}
        >
          {error ? (
            <div className="text-center py-4">
              <p className="text-muted">
                Error loading members: {error.message}
              </p>
            </div>
          ) : filteredUsers.length === 0 && !loading ? (
            <div className="text-center py-4">
              <p className="text-muted">
                {debouncedSearchTerm
                  ? "No users found matching your search."
                  : "No users available."}
              </p>
            </div>
          ) : (
            <>
              <MembersList
                members={filteredUsers}
                mode="add"
                selectedMembers={selectedMembers}
                onMemberToggle={handleUserToggle}
              />

              {loading && page > 1 && <LoadingBox count={3} minHeight={60} />}

              {hasMore && !debouncedSearchTerm && !loading && (
                <div className="text-center">
                  <Button
                    color="primary"
                    title="Load more users"
                    onClick={loadMoreUsers}
                    disabled={loading}
                    className="MembersModal__loadMore p-0"
                  />
                </div>
              )}
            </>
          )}
        </HandleLoadingState>
      </div>
    </ModalSidebar>
  );
};
